import { useEffect, useState } from 'react'
import { Button } from './ui/Button'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    // Detecta se já está instalado
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
    // @ts-ignore
    const isIOSStandalone = window.navigator.standalone === true
    setIsStandalone(isStandaloneMode || isIOSStandalone)

    // Detecta iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    setIsIOS(iOS)

    // Evento beforeinstallprompt (Android/Chrome/Edge)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      
      // Verifica se o usuário já dispensou o prompt antes
      const dismissed = localStorage.getItem('pwa-install-dismissed')
      if (!dismissed) {
        setShowPrompt(true)
      }
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Mostra prompt para iOS se não estiver instalado
    if (iOS && !isStandaloneMode && !isIOSStandalone) {
      const dismissed = localStorage.getItem('pwa-install-dismissed-ios')
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000)
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        console.log('PWA instalado com sucesso')
      }
      
      setDeferredPrompt(null)
      setShowPrompt(false)
    }
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    if (isIOS) {
      localStorage.setItem('pwa-install-dismissed-ios', 'true')
    } else {
      localStorage.setItem('pwa-install-dismissed', 'true')
    }
  }

  const handleShowAgain = () => {
    localStorage.removeItem('pwa-install-dismissed')
    localStorage.removeItem('pwa-install-dismissed-ios')
    if (deferredPrompt || isIOS) {
      setShowPrompt(true)
    }
  }

  // Não mostra se já estiver instalado
  if (isStandalone) {
    return null
  }

  // Botão para mostrar o prompt novamente (sempre visível)
  if (!showPrompt) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={handleShowAgain}
          className="shadow-lg bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3"
          title="Instalar aplicativo"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
            />
          </svg>
        </Button>
      </div>
    )
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg p-4 z-50 animate-slide-up">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1 text-gray-900 dark:text-white">
              Instalar OrderSystem
            </h3>
            {isIOS ? (
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <p className="mb-2">Para instalar no iOS:</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>
                    Toque no botão <strong>Compartilhar</strong>{' '}
                    <span className="inline-block">
                      <svg className="w-4 h-4 inline" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z" />
                      </svg>
                    </span>
                  </li>
                  <li>Role e selecione "Adicionar à Tela de Início"</li>
                  <li>Toque em "Adicionar"</li>
                </ol>
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Instale o aplicativo para acesso rápido e trabalhe offline!
              </p>
            )}
          </div>
          
          <div className="flex gap-2">
            {!isIOS && deferredPrompt && (
              <Button
                onClick={handleInstallClick}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Instalar
              </Button>
            )}
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="text-gray-600 dark:text-gray-300"
            >
              Agora não
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
